import axios, {
  AxiosError,
  AxiosHeaderValue,
  AxiosRequestConfig,
  AxiosRequestHeaders,
} from 'axios';
import qs from 'qs';
import { languageInterceptor } from './interceptors/languageInterceptor';
import { retryInterceptor } from './interceptors/retryInterceptor';

declare module 'axios' {
  export interface AxiosRequestConfig<D = any> {
    meta?: {
      hasIntlSupport?: boolean;
    };
  }
}

export const apiClient = axios.create({
  // baseURL: joinPath([appConfig.apiBaseUrl]),
  withCredentials: true,
  paramsSerializer: {
    serialize: (params: any) =>
      qs.stringify(params, { arrayFormat: 'brackets' }),
  },
});

languageInterceptor(apiClient);
retryInterceptor(apiClient);

export interface IApiClientRequestOptions {
  headers?: Record<string, AxiosHeaderValue>;
  // Permette di formattare gli array in modo che le API li parsino correttamente
  arrayFormat?: 'brackets' | 'repeat';
  responseType?: 'arraybuffer';
  fullResponse?: boolean;
  baseURL?: string;
}

export async function apiClientRequest<T>(
  config: AxiosRequestConfig,
  options?: IApiClientRequestOptions,
): Promise<T> {
  const serializer = options?.arrayFormat
    ? {
        paramsSerializer: {
          serialize: (params: any) =>
            qs.stringify(params, {
              arrayFormat: options?.arrayFormat ?? 'brackets',
            }),
        },
      }
    : {};

  // TODO Generalizzare
  const rewrittenLocalBaseUrl =
    process.env.NODE_ENV === 'development' &&
    !config.baseURL &&
    !options?.baseURL &&
    config.url?.startsWith('/fwp-archived-submissions-api')
      ? { baseURL: '/' }
      : {};

  const { data, headers } = await apiClient.request({
    ...config,
    ...rewrittenLocalBaseUrl,
    ...(options?.baseURL ? { baseURL: options.baseURL } : {}),
    responseType: options?.responseType,
    headers: {
      ...config.headers,
      ...options?.headers,
    },
    meta: {},
    ...serializer,
  });
  // HACK per avere accesso ai Response Headers in data
  if (options?.fullResponse) {
    return { data, headers } as any;
  }
  return data;
}

export type ErrorType<Error> = AxiosError<Error>;

/**
 * Utility per parsare la risposta completa di una chiamata
 * con apiClientRequest in modalità `fullResponse`. Al momento
 * il tipo di ritorno è errato per poter accedere agli headers
 * e la gestione del risultato è manuale.
 */
export function parseApiClientFullResponse<T>(data: T) {
  if (!data) return { data: undefined, headers: undefined };
  const { data: _data, headers } = data as unknown as {
    data: any;
    headers: AxiosRequestHeaders;
  };

  return { data: _data, headers };
}
